import { useEffect, useState } from 'react';
import { Form, Row, Col, Input, DatePicker, Button, Select, TimePicker, message } from 'antd';
import { EQUIPMENT_TYPE_OPTIONS, FUEL_TYPE_OPTIONS, BILLING_CYCLE_OPTIONS, USER_ROLE_OPTIONS, UNIT_OF_MEASURE_OPTIONS, ROLES } from "../constants";
import apiClient from '../helpers/apiHelper';
import { ENDPOINTS, REQUEST_TYPES } from '../helpers/apiConfig';
import TextArea from 'antd/es/input/TextArea';


export const EquipmentForm = ({
    user,
    form,
    onSubmit,
    isUpdate,
    loading,
    modalOpen,
    userVersion,
    onSaveDraft,
}) => {

    const [plantUsers, setPlantUsers] = useState([]);
    const [owners, setOwners] = useState([]);
    const [plantUsersByHod, setPlantUsersByHod] = useState([]);

    const [costCenterOptions, setCostCenterOptions] = useState([]);
    const [vehicleHiringPurpose, setVehicleHiringPurpose] = useState([]);
    const [companywiseEquipments, setCompanywiseEquipments] = useState([]);

    const fetchCompanywiseEquipments = async () => {
        try {
            const response = await apiClient(ENDPOINTS.OPTIONS.FETCH_COMPANYWISE_EQUIPMENTS, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_COMPANYWISE_EQUIPMENTS,
                }),
            });
            setCompanywiseEquipments(response?.equipments || []);
        } catch (error) {
            message.error("Failed to fetch company-wise equipments");
        }
    };

    const fetchCostCenterOptions = async () => {
        try {
            const response = await apiClient(ENDPOINTS.OPTIONS.FETCH_COST_CENTER_OPTIONS, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_COST_CENTER_OPTIONS,
                }),
            });
            setCostCenterOptions(response?.cost_center_options || []);
        } catch (error) {
            message.error("Failed to fetch cost center options");
        }
    };

    const fetchVehicleHiringPurpose = async () => {
        try {
            const response = await apiClient(ENDPOINTS.OPTIONS.FETCH_VEHICLE_HIRING_PURPOSE, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_VEHICLE_HIRING_PURPOSE,
                }),
            });
            setVehicleHiringPurpose(response?.vehicle_hiring_purpose || []);
        } catch (error) {
            message.error("Failed to fetch vehicle hiring purpose options");
        }
    };

    const fetchPlantUsers = async () => {
        const response = await apiClient(ENDPOINTS.USER.FETCH_PLANT_USERS, {
            method: 'POST',
            body: JSON.stringify({
                request_type: REQUEST_TYPES.FETCH_PLANT_USERS
            })
        });

        setPlantUsers(response?.plant_users);
    };

    const fetchOwners = async () => {
        const response = await apiClient(ENDPOINTS.USER.FETCH_OWNERS, {
            method: 'POST',
            body: JSON.stringify({
                request_type: REQUEST_TYPES.FETCH_OWNERS
            })
        });
        setOwners(response?.owners);
    }

    const fetchPlantUsersByHod = async () => {
        try {
            const res = await apiClient(ENDPOINTS.USER.FETCH_USERS, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_USERS
                })
            });

            const userList = res?.items || [];
            setPlantUsersByHod(userList);

        } catch (error) {
            message.error("Failed to fetch plant users by HOD");
        }
    }

    useEffect(() => {
        if (modalOpen) {
            user?.role == ROLES.SPEND_TEAM && fetchPlantUsers();
            user?.role == ROLES.PLANT_HOD && fetchPlantUsersByHod();
            fetchOwners();
            fetchCostCenterOptions();
            fetchVehicleHiringPurpose();
            if (!isUpdate) {
                fetchCompanywiseEquipments();
            }
        }
    }, [modalOpen, userVersion])

    useEffect(() => {
        if (user?.role === ROLES.PLANT_USER && !isUpdate) {
            form.setFieldsValue({
                plant_user: user.email,
                plant_hod: user.plant_hod,
            });
        }
    }, [user, form, isUpdate]);

    return (
        <Form form={form} layout="vertical" className="bold-labels" onFinish={onSubmit}>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="equipment_type" label="Equipment Type" rules={[{ required: true, message: "Equipment type is required" }]}>
                        <Select placeholder="Select equipment type" disabled={isUpdate} showSearch>
                            {EQUIPMENT_TYPE_OPTIONS.map((type) => (
                                <Select.Option key={type.value} value={type.value}>
                                    {type.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Equipment Name"
                        name="equipment_name"
                        rules={[{ required: true, message: "Equipment name is required" }]}
                    >
                        <Input placeholder="Enter equipment name" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Vehicle No."
                        name="vehicle_no"
                        rules={[{ required: true, message: "Vehicle no. is required" }]}
                    >
                        <Select
                            placeholder="Select vehicle number"
                            disabled={isUpdate}
                            showSearch
                            optionFilterProp="value"
                            onChange={(selectedVehicleNo) => {
                                const selected = companywiseEquipments.find(
                                    (item) => item.vehicle_no === selectedVehicleNo
                                );
                                form.setFieldsValue({ gps_imei_no: selected?.imei_no || "" });
                            }}
                        >
                            {companywiseEquipments.map((item) => (
                                    <Select.Option key={item.vehicle_no} value={item.vehicle_no}>
                                        {item.vehicle_no}
                                    </Select.Option>
                                ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="GPS IMEI No."
                        name="gps_imei_no"
                    >
                        <Input placeholder="Auto-populated from vehicle number" disabled />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Fuel Type"
                        name="fuel_type"
                        rules={[{ required: true, message: "Fuel type is required" }]}
                    >
                        <Select placeholder="Select fuel type" showSearch>
                            {FUEL_TYPE_OPTIONS.map((type) => (
                                <Select.Option key={type.value} value={type.value}>
                                    {type.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Vehicle Hiring Purpose"
                        name="hiring_purpose"
                        rules={[{ required: true, message: "Vehicle hiring purpose is required" }]}
                    >
                        <Select placeholder="Select hiring purpose" showSearch>
                            {vehicleHiringPurpose.map((type) => (
                                <Select.Option key={type.label} value={type.label}>
                                    {type.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Manufacturer Name"
                        name="manufacturer"
                        rules={[{ required: true, message: "Manufacturer name is required" }]}
                    >
                        <Input placeholder="Enter manufacturer name" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Manufaturing Year"
                        name="manufacturing_year"
                        rules={[{ required: true, message: "Manufacturing year is required" }]}
                    >
                        <Input placeholder="Enter manufacturing year" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Plant Name"
                        name="plant_name"
                        rules={[{ required: true, message: "Plant name is required" }]}
                    >
                        <Input placeholder="Enter plant name" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Deployment Area"
                        name="deployment_area"
                        rules={[{ required: true, message: "Deployment area is required" }]}
                    >
                        <Input placeholder="Enter deployment area" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Plant User ID"
                        name="plant_user"
                        rules={[{ required: true, message: "Plant user ID is required" }]}
                        initialValue={user?.role == ROLES.PLANT_USER && user?.email}
                    >
                        <Select
                            placeholder="Select plant user"
                            disabled={user?.role == ROLES.PLANT_USER}
                            onChange={(selectedEmail) => {
                                const selectedObj = plantUsers.find((item) => item.email === selectedEmail);
                                if (selectedObj) {
                                    form.setFieldsValue({ plant_hod: selectedObj.plant_hod });
                                }
                            }}
                            showSearch
                        >
                            {(user?.role === ROLES.SPEND_TEAM ? plantUsers : plantUsersByHod)?.map((type) => (
                                <Select.Option key={type.email} value={type.email}>
                                    {type.email}
                                </Select.Option>
                            ))}

                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Plant HOD ID"
                        name="plant_hod"
                        rules={[{ required: true, message: "Plant HOD ID is required" }]}
                        initialValue={user?.role == ROLES.PLANT_USER ? user?.plant_hod : user?.role == ROLES.PLANT_HOD ? user?.email : ""}
                    >
                        <Input placeholder="Plant HOD ID" disabled />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Driver Mobile No."
                        name="driver_mobile_no"
                        rules={[{ required: true, message: "drive mobile no. is required" }]}
                    >
                        <Input placeholder="Enter drive mobile number" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Owner Email ID"
                        name="owner_email"
                        rules={[{ required: true, message: "Owner email ID is required" }]}
                    >
                        <Select placeholder="Select owner ID" showSearch>
                            {owners.map((type) => (
                                <Select.Option key={type.email} value={type.email}>
                                    {type.email}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="AMNS WO No."
                        name="amns_wo"
                        rules={[{ required: true, message: "AMNS WO no. is required" }]}
                    >
                        <Input placeholder="Enter AMNS WO number" type='Number' />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Line Item No."
                        name="line_item_no"
                        rules={[{ required: true, message: "Line item no. is required" }]}
                    >
                        <Input placeholder="Enter line item no." type='Number' />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Cost Center"
                        name="cost_center"
                        rules={[{ required: true, message: "Cost center is required" }]}
                    >
                        <Select placeholder="Select cost center" showSearch>
                            {costCenterOptions.map((center) => (
                                <Select.Option key={center.label} value={center.label}>
                                    {center.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Billing Cycle"
                        name="billing_cycle"
                        rules={[{ required: true, message: "Billing cycle is required" }]}
                    >
                        <Select placeholder="Select billing cycle" showSearch>
                            {BILLING_CYCLE_OPTIONS.map((type) => (
                                <Select.Option key={type.value} value={type.value}>
                                    {type.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Unit Of Measure"
                        name="unit_of_measure"
                        rules={[{ required: true, message: "Unit of measure is required" }]}
                    >
                        <Select placeholder="Select unit of measure" showSearch>
                            {UNIT_OF_MEASURE_OPTIONS.map((type) => (
                                <Select.Option key={type.value} value={type.value}>
                                    {type.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Contract Rate (In INR)"
                        name="contract_rate"
                        rules={[{ required: true, message: "Contract rate is required" }]}
                    >
                        <Input placeholder="Enter contract rate" type='Number' />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        className="red-picker-scope"
                        label="Service Date Duration"
                        name="date_range"
                        rules={[{ required: true, message: "Service start and end date is required" }]}
                    >
                        <DatePicker.RangePicker className="w-full red-picker" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        className="red-picker-scope"
                        label="Service Time Duration"
                        name="time_range"
                    >
                        <TimePicker.RangePicker
                            className="w-full red-picker"
                            format="HH:mm"
                            minuteStep={30}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item
                        label="Additional Remarks"
                        name="additional_remarks"
                    >
                        <TextArea placeholder="Enter Additional remarks" />
                    </Form.Item>
                </Col>
            </Row>

            <Row justify="end" gutter={16} className="mt-2">
                <Col>
                    <Button onClick={() => {
                        form.resetFields();
                        if (user?.role === ROLES.PLANT_USER) {
                            form.setFieldsValue({
                                plant_user: user.email,
                                plant_hod: user.plant_hod,
                            });
                        }
                    }}>Reset</Button>
                </Col>
                {!isUpdate && onSaveDraft && (
                    <Col>
                        <Button onClick={onSaveDraft}>
                            Save as Draft
                        </Button>
                    </Col>
                )}
                <Col>
                    <Button
                        type="primary"
                        htmlType="submit"
                        danger
                        loading={loading}
                    >
                        {isUpdate ? "Update" : "Submit"}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export const UserForm = ({
    user,
    form,
    onSubmit,
    isUpdate,
    loading,
    modalOpen,
}) => {
    const userRole = user?.role;
    const selectedRole = Form.useWatch('role', form);
    const emailValue = Form.useWatch('email', form);
    const [plantHods, setPlantHods] = useState([]);

    // Determine which role is being created based on who's logged in
    const getEffectiveRole = () => {
        if (userRole === ROLES.PLANT_USER) return ROLES.OWNER;
        if (userRole === ROLES.PLANT_HOD) return ROLES.PLANT_USER;
        return selectedRole; // Spend team picks from dropdown
    };

    const fetchHods = async () => {
        try {
            const response = await apiClient(ENDPOINTS.USER.FETCH_PLANT_HODS, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_PLANT_HODS
                })
            });

            setPlantHods(response?.plant_hods || []);
        } catch (error) {
            message.error("Failed to fetch plant HODs");
        }
    }

    const effectiveRole = getEffectiveRole();

    // Filter out the current email from HOD list (prevents assigning self as HOD)
    const filteredHods = plantHods.filter(
        (hod) => hod.email !== emailValue
    );

    useEffect(() => {
        if (userRole === ROLES.PLANT_HOD && !isUpdate) {
            form.setFieldsValue({ plant_hod: user?.email });
        }
    }, [user, form, userRole, isUpdate]);

    // Fetch latest HOD list whenever effective role becomes "Plant user" or modal opens
    useEffect(() => {
        if (modalOpen && effectiveRole === ROLES.PLANT_USER) {
            fetchHods();
        }
    }, [effectiveRole, modalOpen]);

    return (
        <Form form={form} layout="vertical" className="bold-labels"
            onFinish={onSubmit}
            initialValues={userRole === ROLES.PLANT_HOD ? { plant_hod: user?.email } : {}}
        >

            {/* Spend team can pick any role */}
            {userRole === ROLES.SPEND_TEAM && (
                <Form.Item name="role" label="Role" rules={[{ required: true, message: "Role is required" }]}>
                    <Select placeholder="Select a role">
                        {USER_ROLE_OPTIONS.map((role) => (
                            <Select.Option key={role.value} value={role.value}>
                                {role.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            )}

            <Row gutter={16}>
                {/* Plant user creates Owner → show Full Name field */}
                {effectiveRole === ROLES.OWNER && (
                    <Col span={12}>
                        <Form.Item
                            label="Full Name"
                            name="full_name"
                            rules={[{ required: true, message: "Full name is required" }]}
                        >
                            <Input placeholder="Enter full name" />
                        </Form.Item>
                    </Col>
                )}

                <Col span={12}>
                    <Form.Item
                        label="Email ID"
                        name="email"
                        rules={[
                            { required: true, message: "Email ID is required" },
                            { type: 'email', message: "Please enter a valid email" }
                        ]}
                    >
                        <Input placeholder="Enter email ID" disabled={isUpdate} />
                    </Form.Item>
                </Col>

                {/* Plant HOD creates Plant user → show Plant HOD field (auto-filled, disabled) */}
                {effectiveRole === ROLES.PLANT_USER && (
                    <Col span={12}>
                        <Form.Item
                            label="Plant HOD ID"
                            name="plant_hod"
                            rules={[{ required: true, message: "Plant HOD ID is required" }]}
                        >
                            {userRole === ROLES.PLANT_HOD ? (
                                <Input placeholder="Plant HOD ID" disabled />
                            ) : (
                                <Select placeholder="Select Plant HOD" showSearch>
                                    {filteredHods.map((hod) => (
                                        <Select.Option key={hod.email} value={hod.email}>
                                            {hod.email}
                                        </Select.Option>
                                    ))}
                                </Select>
                            )}
                        </Form.Item>
                    </Col>
                )}
            </Row>

            <Row justify="end" gutter={16} className="mt-2">
                <Col>
                    <Button onClick={() => {
                        form.resetFields();
                        if (userRole === ROLES.PLANT_HOD) {
                            form.setFieldsValue({ plant_hod: user?.email });
                        }
                    }}>Reset</Button>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        htmlType="submit"
                        danger
                        loading={loading}
                    >
                        {isUpdate ? "Update" : "Submit"}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
}
